export async function createHtml(data, style, accentColor = "#111111") {
  const PAGE_WIDTH = 794;
  const PAGE_SCAFFOLD_CSS = `
html {
  /* Allow some smart text scaling on small devices */
  -webkit-text-size-adjust: 100%;
}

body {
  margin: 0;
}

/* Screen: fixed-width A4-style page centred */
@media screen {
  body {
    background: #fffffff;
    transform: scale(0.90);
  }

  .page {
    width: ${PAGE_WIDTH}px;
    margin: 0 auto;
    min-height: 100vh;

    box-sizing: border-box;
  }
}

/* Print: let the browser do true A4 layout */
@media print {
  body {
    background: #ffffff;
  }
  .page {
    width: auto;
    transform: none;
    margin: 0;
  }
}
`;

  const injectPageShell = (doc) => {
    let out = doc;

    const VIEWPORT_META = `<meta name="viewport" content="width=${PAGE_WIDTH}">`;

    if (out.match(/<\/style>/i)) {
      out = out.replace(/<\/style>/i, `${PAGE_SCAFFOLD_CSS}\n</style>`);
    } else if (out.match(/<\/head>/i)) {
      out = out.replace(
        /<\/head>/i,
        `<style>${PAGE_SCAFFOLD_CSS}</style>\n</head>`
      );
    } else {
      out = `<head><style>${PAGE_SCAFFOLD_CSS}</style></head>${out}`;
    }

    if (out.match(/<head>/i)) {
      out = out.replace(/<head>/i, `<head>\n${VIEWPORT_META}`);
    } else {
      out = `${VIEWPORT_META}${out}`;
    }

    if (!out.includes('class="page"')) {
      out = out.replace(/<body([^>]*)>/i, `<body$1><div class="page">`);
      out = out.replace(/<\/body>/i, `</div></body>`);
    }

    return out;
  };

  let html;
  switch (style) {
    case "corporate":
      html = createCorporateHtml(data, accentColor);
      break;
    case "creative":
      html = createCreativeHtml(data, accentColor);
      break;
    case "formal":
    case "legal":
      html = createFormalHtml(data);
      break;
    case "old":
      html = createOldHtml(data);
      break;
    case "tech":
      html = createTechHtml(data, accentColor);
      break;
    case "modern":
    default:
      html = createModernHtml(data, accentColor);
      break;
  }

  return injectPageShell(html);
}

function fmt(n) {
  return (Math.round(Number(n || 0) * 100) / 100).toFixed(2);
}

function currency(n) {
  return `£${fmt(n)}`;
}

function escapeHtml(s) {
  return s
    ? String(s)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;")
    : "";
}

function resolveLogoUrl(raw) {
  if (!raw) return null;
  const s = String(raw).trim();

  if (/^(https?:|data:|blob:)/i.test(s)) {
    return s;
  }

  if (typeof window !== "undefined") {
    try {
      return new URL(s, window.location.origin).href;
    } catch {
      return s;
    }
  }

  return s;
}

function safe(value) {
  if (value == null) return "";
  if (typeof value === "boolean") return "";
  const s = String(value).trim();
  return s ? escapeHtml(s) : "";
}

function lines(...parts) {
  const prepared = parts
    .map((part) => {
      if (Array.isArray(part)) {
        return part
          .map((p) => safe(p))
          .filter(Boolean)
          .join(", ");
      }
      return safe(part);
    })
    .filter(Boolean);

  return prepared.join("<br/>");
}

function paymentFields(data) {
  const rows = [];
  if (safe(data.payee)) rows.push({ label: "Payee", value: safe(data.payee) });
  if (safe(data.sortCode))
    rows.push({ label: "Sort Code", value: safe(data.sortCode) });
  if (safe(data.accountNumber))
    rows.push({ label: "Account", value: safe(data.accountNumber) });
  if (safe(data.invoiceNumber))
    rows.push({ label: "Reference", value: safe(data.invoiceNumber) });
  return rows;
}
function logoHtml(data, size = 70) {
  const url = resolveLogoUrl(data.logoUri);

  if (url) {
    return `<img src="${escapeHtml(
      url
    )}" style="width:${size}px;height:${size}px;object-fit:contain;border-radius:8px;"/>`;
  }

  const initials = escapeHtml(
    (data.companyName || "")
      .split(" ")
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "LOGO"
  );
  return `<div style="background: var(--accent, ${escapeHtml(
    "#111"
  )}); color:#fff; width:${size}px; height:${size}px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-weight:700; font-family:Arial, sans-serif;">${initials}</div>`;
}

function itemsRows(data, opts = {}) {
  const { showUnit = false } = opts || {};
  const items = data.items || [];

  return items
    .map((it) => {
      const rawDesc = it.description || "";
      let jobName = it.jobName ?? null;
      let subDesc = null;

      if (jobName) {
        let tmpDesc = rawDesc;
        let idx = tmpDesc.indexOf(" – ");
        if (idx === -1) idx = tmpDesc.indexOf(" - ");
        if (idx > 0) {
          subDesc = tmpDesc.slice(idx + 3).trim();
        } else {
          subDesc = tmpDesc.trim();
        }
      } else {
        let tmpDesc = rawDesc;
        let idx = tmpDesc.indexOf(" – ");
        if (idx === -1) idx = tmpDesc.indexOf(" - ");
        if (idx > 0) {
          jobName = tmpDesc.slice(0, idx).trim();
          subDesc = tmpDesc.slice(idx + 3).trim();
        } else {
          subDesc = tmpDesc.trim();
        }
      }

      let displayDesc;
      if (jobName) {
        if (subDesc) {
          displayDesc =
            `<strong>${escapeHtml(jobName)}</strong>` +
            (subDesc ? ` – ${escapeHtml(subDesc)}` : "");
        } else {
          displayDesc = `<strong>${escapeHtml(jobName)}</strong>`;
        }
      } else {
        displayDesc = escapeHtml(rawDesc);
      }

      const qty = it.qty ?? 0;
      const unitPrice = it.unitPrice ?? 0;
      const amount = qty * unitPrice;
      const price = currency(unitPrice);
      const total = currency(amount);

      return `
        <tr>
          <td>${displayDesc}</td>
          <td class="right">${qty}</td>
          ${showUnit ? '<td class="right">pcs</td>' : ""}
          <td class="right">${price}</td>
          <td class="right">${total}</td>
        </tr>`;
    })
    .join("");
}

function totalsBlock(data, options = {}) {
  const {
    labelTotal = "Total Due",
    withSubtotal = true,
    withDiscount = true,
    withDeposit = true,
  } = options;
  const subtotal =
    data.subtotal ??
    (data.items || []).reduce(
      (s, it) => s + (Number(it.qty) || 0) * (Number(it.unitPrice) || 0),
      0
    );
  const discount = Number(data.discount) || 0;
  const deposit = Number(data.deposit) || 0;
  const total = data.total ?? Math.max(0, subtotal - discount - deposit);

  const showDeposit = withDeposit && deposit > 0;
  const showDiscount = withDiscount && discount > 0;

  return `
    <div class="summary">
      ${
        withSubtotal
          ? `<div class="summary-row"><span>Subtotal</span><span>${currency(
              subtotal
            )}</span></div>`
          : ""
      }
      ${
        showDiscount
          ? `<div class="summary-row"><span>Discount</span><span>-${currency(
              discount
            )}</span></div>`
          : ""
      }
      ${
        showDeposit
          ? `<div class="summary-row"><span>Deposit</span><span>-${currency(
              deposit
            )}</span></div>`
          : ""
      }
      <div class="summary-row total"><span>${escapeHtml(
        labelTotal
      )}</span><span>${currency(total)}</span></div>
    </div>`;
}

function createBookingHtml(data, accentColor) {
  const accent = accentColor || "#1e3a8a";
  return `
<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Travel Invoice</title>
    <style>
      :root { --accent: ${escapeHtml(
        accent
      )}; --accent-light: #f3f4f6; --highlight: #cdaa7d; --text: #222; --muted: #6b7280; --border: #e5e7eb; --bg: #ffffff; }
      @page { size: A4; margin: 25mm; }
      @media screen {
        body .page { width: 900px; max-width: 100%; }
      }
      body { font-family: 'Helvetica Neue', Arial, sans-serif; background: var(--bg); color: var(--text); margin: 0; padding: 40px 50px; }
      .invoice-wrapper { width: 100%; max-width: 900px; margin: 0 auto; border: 2px dashed var(--accent); border-radius: 20px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.05); display:flex; flex-direction:column; min-height:100vh; box-sizing:border-box; }
      .header { background: var(--accent); color: white; display: flex; justify-content: space-between; align-items: center; padding: 25px 40px; gap:16px; }
      .company { font-size: 24px; font-weight: 700; letter-spacing: .5px; }
      .invoice-title { font-size: 36px; font-weight: 800; letter-spacing: 1px; }
      .info-section { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; padding: 40px; border-bottom: 2px dashed var(--border); background: var(--accent-light); }
      .info-box { line-height: 1.7; font-size: 14px; }
      .info-box strong { display: block; font-weight: 700; color: var(--accent); margin-bottom: 6px; letter-spacing: .02em; }
      .mini-meta { margin-top: 10px; color: var(--muted); }
      .perforation { border-top: 2px dashed var(--border); margin: 0 40px; }
      .booking { padding: 40px; flex:1; display:flex; flex-direction:column; gap:16px; }
      table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 14px; }
      th, td { padding: 12px 8px; border-bottom: 1px solid var(--border); }
      th { text-align: left; color: var(--muted); font-weight: 600; background: #fafafa; }
      .right { text-align: right; }
      .summary { margin-top: 8px; border-top: 2px solid var(--accent); padding-top: 10px; }
      .summary-row { display: flex; justify-content: space-between; margin: 6px 0; font-size: 14px; }
      .summary-row.total { font-size: 18px; font-weight: 700; color: var(--accent); border-top: 2px solid var(--highlight); padding-top: 8px; }
      .notes { margin-top: 8px; background:#fffaf0; border:1px solid var(--border); border-radius:12px; padding:14px 16px; display:grid; gap:10px; }
      .notes strong { color:var(--accent); }
      footer { background: var(--accent); color: white; padding: 30px 40px; display: flex; justify-content: space-between; font-size: 13px; border-top: 2px dashed var(--highlight); gap:24px; margin-top:auto; }
      footer div { flex:1; line-height: 1.6; }
      .barcode { text-align: right; font-family: monospace; font-size: 18px; letter-spacing: 4px; color: rgba(255,255,255,0.7); }
      .thankyou { text-align: center; color: var(--accent); font-weight: 600; margin: 30px 0 10px; font-size: 16px; }
    </style>
  </head>
  <body>
    <div class="invoice-wrapper">
      <div class="header">
        <div style="display:flex;align-items:center;gap:16px;">
          ${logoHtml(data, 56)}
          <div class="company">${escapeHtml(data.companyName)}</div>
        </div>
        <div class="invoice-title">INVOICE</div>
      </div>
      <div class="info-section">
        <div class="info-box">
          <strong>From</strong>
          ${lines(data.companyName, data.address, [data.city, data.postCode])}
        </div>
        <div class="info-box">
          <strong>To</strong>
          ${lines(
            data.customerName,
            data.customerAddress,
            [data.customerCity, data.customerPostCode],
            data.email,
            data.phoneNumber
          )}
          ${
            lines(
              data.invoiceNumber && `Invoice No: ${data.invoiceNumber}`,
              data.invoiceDate && `Invoice Date: ${data.invoiceDate}`,
              data.suppliedDate && `Due Date: ${data.suppliedDate}`
            )
              ? `<div class="mini-meta">${lines(
                  data.invoiceNumber && `Invoice No: ${data.invoiceNumber}`,
                  data.invoiceDate && `Invoice Date: ${data.invoiceDate}`,
                  data.suppliedDate && `Due Date: ${data.suppliedDate}`
                )}</div>`
              : ""
          }
        </div>
      </div>
      <div class="perforation"></div>
      <div class="booking">
        <strong style="font-size:16px;color:var(--accent)">Details</strong>
        <table>
          <thead>
            <tr>
              <th>Item</th>
              <th class="right">Qty</th>
              <th class="right">Price</th>
              <th class="right">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${itemsRows(data)}
        </tbody>
        </table>
        ${totalsBlock(data)}
        ${
          data.notes || data.terms
            ? `<div class="notes">
              ${
                data.notes
                  ? `<div><strong>Notes</strong><div>${escapeHtml(
                      data.notes
                    )}</div></div>`
                  : ""
              }
              ${
                data.terms
                  ? `<div><strong>Terms</strong><div>${escapeHtml(
                      data.terms
                    )}</div></div>`
                  : ""
              }
            </div>`
            : ""
        }
      </div>
      <footer>
        ${
          paymentFields(data).length
            ? `<div>
              <strong>Payment Details</strong><br/>
              ${paymentFields(data)
                .map((row) => `${row.label}: ${row.value}`)
                .join("<br/>")}
            </div>`
            : ""
        }
        ${
          lines(data.email, data.phoneNumber)
            ? `<div>
              <strong>Contact</strong><br/>
              ${lines(data.email, data.phoneNumber)}
            </div>`
            : ""
        }
      </footer>
    </div>
  </body>
</html>`;
}

function createCorporateHtml(data, accentColor) {
  const accent = accentColor || "#0a2342";
  return `
<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Corporate Premium Invoice</title>
    <style>
      :root { --accent: ${escapeHtml(
        accent
      )}; --accent-light:#133b63; --highlight:#cba135; --text:#1c1c1c; --muted:#6b7280; --border:#e5e7eb; --bg:#ffffff; }
      @page { size: A4; margin: 25mm; }
      @media screen {
        .page {
          transform: scale(0.80);
          transform-origin: top center;
        }
      }
      body { font-family:'Georgia','Times New Roman',serif; color:var(--text); background:var(--bg); margin:0; padding:50px 60px; position:relative; min-height:100vh; display:flex; flex-direction:column; }
      .content { flex:1; display:flex; flex-direction:column; gap:26px; }
      .header { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:3px solid var(--accent); padding-bottom:25px; }
      .company { font-size:26px; font-weight:700; color:var(--accent); }
      .slogan { font-size:13px; color:var(--muted); font-style:italic; margin-top:4px; }
      .invoice-title { font-size:44px; font-weight:700; color:var(--highlight); text-transform:uppercase; letter-spacing:1px; }
      .meta { display:flex; justify-content:space-between; margin-top:20px; font-size:14px; gap:24px; }
      .meta div { width:48%; line-height:1.7; }
      strong { font-weight:600; color:var(--accent); }
      table { width:100%; border-collapse:collapse; margin-top:40px; font-size:14px; }
      th, td { padding:12px 10px; border-bottom:1px solid var(--border); }
      th { background:#f9fafb; text-align:left; color:var(--accent); font-weight:600; border-top:2px solid var(--accent); }
      .right { text-align:right; }
      .summary { margin-top:30px; border-top:2px solid var(--accent); padding-top:15px; width:100%; font-size:14px; }
      .summary-row { display:flex; justify-content:space-between; margin:6px 0; }
      .summary-row.total { font-size:18px; font-weight:700; color:var(--highlight); border-top:2px solid var(--highlight); padding-top:8px; }
      .notes-box { background:#f9fafb; border:1px solid var(--border); border-radius:12px; padding:14px 16px; display:grid; gap:10px; }
      .notes-box strong { color:var(--accent); }
      footer { margin-top:auto; border-top:3px solid var(--accent); padding-top:20px; display:flex; justify-content:space-between; color:var(--muted); font-size:13px; gap:18px; flex-wrap:wrap; }
      footer div { flex:1; min-width:220px; }
      .watermark { position:absolute; top:45%; left:50%; transform:translate(-50%, -50%) rotate(-30deg); font-size:120px; color: rgba(10,35,66,0.05); font-weight:700; pointer-events:none; white-space:nowrap; }
      .pay-btn { display:inline-block; margin-top:25px; padding:10px 22px; background:var(--accent); color:#fff; text-decoration:none; border-radius:6px; font-weight:600; font-size:14px; }
    </style>
  </head>
  <body>
    <div class="watermark">${escapeHtml(
      (data.companyName || "").toUpperCase()
    )}</div>
    <div class="content">
      <div class="header">
        <div style="display:flex;gap:16px;align-items:center;">
          ${logoHtml(data, 60)}
          <div>
            <div class="company">${escapeHtml(data.companyName)}</div>
          
          </div>
        </div>
        <div class="invoice-title">Invoice</div>
      </div>

      <div class="meta">
        <div>
          <strong>From</strong><br/>
          ${lines(data.companyName, data.address, [data.city, data.postCode])}
        </div>
        <div>
          <strong>To</strong><br/>
          ${lines(
            data.customerName,
            data.customerAddress,
            [data.customerCity, data.customerPostCode],
            data.email,
            data.phoneNumber
          )}
        </div>
      </div>

      <div class="meta" style="margin-top:0">
        <div>
          ${
            data.invoiceNumber
              ? `<div><strong>Invoice No:</strong> ${safe(
                  data.invoiceNumber
                )}</div>`
              : ""
          }
          ${
            data.invoiceDate
              ? `<div><strong>Invoice Date:</strong> ${safe(
                  data.invoiceDate
                )}</div>`
              : ""
          }
          ${
            data.suppliedDate
              ? `<div><strong>Due Date:</strong> ${safe(
                  data.suppliedDate
                )}</div>`
              : ""
          }
        </div>
        <div>
          ${
            paymentFields(data).length
              ? `<strong>Payment Info</strong><br/>
          ${paymentFields(data)
            .map((row) => `${row.label}: ${row.value}`)
            .join("<br/>")}`
              : ""
          }
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>Description</th>
            <th class="right">Qty</th>
            <th class="right">Rate</th>
            <th class="right">Total</th>
          </tr>
        </thead>
        <tbody>
          ${itemsRows(data)}
        </tbody>
      </table>

      ${totalsBlock(data)}

      ${
        data.notes || data.terms
          ? `<div class="notes-box">
              ${
                data.notes
                  ? `<div><strong>Notes</strong><div>${escapeHtml(
                      data.notes
                    )}</div></div>`
                  : ""
              }
              ${
                data.terms
                  ? `<div><strong>Terms</strong><div>${escapeHtml(
                      data.terms
                    )}</div></div>`
                  : ""
              }
            </div>`
          : ""
      }
    </div>

    <footer>
      <div><strong>Registered Office</strong><br/>${lines(data.address, [
        data.city,
        data.postCode,
      ])}</div>
      ${
        lines(data.email, data.phoneNumber)
          ? `<div><strong>Contact</strong><br/>${lines(
              data.email,
              data.phoneNumber
            )}</div>`
          : ""
      }
    </footer>
  </body>
</html>`;
}

function createCreativeHtml(data, accentColor) {
  const accent = accentColor || "#e56b6f";
  const deposit = Number(data.deposit) || 0;

  return `
<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Creative Studio Invoice</title>
    <style>
      :root { --accent:${escapeHtml(
        accent
      )}; --text:#222; --muted:#6b6b6b; --border:#e5e5e5; --bg:#fffdfd; }
      @page { size: A4; margin: 25mm; }
      @media screen {
        .page {
          transform: scale(0.80);
          transform-origin: top center;
        }
      }
      body { font-family:'Poppins','Helvetica Neue',Arial,sans-serif; background:var(--bg); color:var(--text); margin:0; padding:50px; }
      .invoice-wrapper { max-width: 850px; margin:0 auto; min-height:100vh; display:flex; flex-direction:column; }
      .header { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:3px solid var(--accent); padding-bottom:20px; }
      .studio-name { font-size:26px; font-weight:700; letter-spacing:-.5px; }
      .tagline { font-size:13px; color:var(--muted); margin-top:2px; }
      .invoice-title { font-size:48px; font-weight:800; color:var(--accent); letter-spacing:-1px; }
      .info-grid { display:grid; grid-template-columns:1fr 1fr; margin-top:40px; gap:40px; }
      .info-box { font-size:14px; line-height:1.8; }
      .info-box strong { display:block; font-weight:600; color:var(--text); }
      table { width:100%; border-collapse:collapse; margin-top:40px; }
      th, td { padding:12px 10px; border-bottom:1px solid var(--border); font-size:14px; }
      th { text-align:left; color:var(--muted); font-weight:600; }
      .right { text-align:right; }
      .summary { margin-top:20px; width:100%; }
      .summary-row { display:flex; justify-content:space-between; margin:4px 0; font-size:14px; }
      .summary-row.total { font-size:20px; font-weight:700; color:var(--accent); margin-top:10px; }
      .notes-box { margin-top:20px; background:#fffaf0; border:1px solid var(--border); border-radius:12px; padding:14px 16px; display:grid; gap:10px; }
      .notes-box strong { color:var(--accent); }
      footer { margin-top:auto; border-top:2px solid var(--border); padding-top:20px; display:flex; justify-content:space-between; font-size:13px; color:var(--muted); gap:16px; flex-wrap:wrap; }
      footer div { flex:1; min-width:200px; }
      .thank-you { margin-top:40px; text-align:center; font-family:'Dancing Script', cursive; font-size:22px; color:var(--accent); }
    </style>
  </head>
  <body>
    <div class="invoice-wrapper">
      <div class="header">
        <div style="display:flex;align-items:center;gap:16px;">
          ${logoHtml(data, 60)}
          <div>
            <div class="studio-name">${escapeHtml(data.companyName)} </div>
           
          </div>
        </div>
       
      </div>

      <div class="info-grid">
        <div class="info-box">
          <strong>From</strong>
          ${lines(data.companyName, data.address, [data.city, data.postCode])}
        </div>
        <div class="info-box">
          <strong>To</strong>
          ${lines(
            data.customerName,
            data.customerAddress,
            [data.customerCity, data.customerPostCode],
            data.email,
            data.phoneNumber
          )}
        </div>
      </div>

      <div class="info-grid" style="margin-top:20px;">
        <div class="info-box">
          ${
            data.invoiceNumber
              ? `<div><strong>Invoice Number</strong> ${safe(
                  data.invoiceNumber
                )}</div>`
              : ""
          }
          ${
            data.invoiceDate
              ? `<div><strong>Date Issued</strong> ${safe(
                  data.invoiceDate
                )}</div>`
              : ""
          }
          ${
            data.suppliedDate
              ? `<div><strong>Due Date</strong> ${safe(
                  data.suppliedDate
                )}</div>`
              : ""
          }
        </div>
        <div class="info-box">
          <strong>Payment Info</strong>
          ${
            paymentFields(data).length
              ? paymentFields(data)
                  .map((row) => `${row.label}: ${row.value}`)
                  .join("<br/>")
              : ""
          }
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>Item Description</th>
            <th class="right">Qty</th>
            <th class="right">Rate</th>
            <th class="right">Total</th>
          </tr>
        </thead>
        <tbody>
          ${itemsRows(data)}
        </tbody>
      </table>

      <div class="summary">
        <div class="summary-row"><span>Subtotal</span><span>${currency(
          data.subtotal ?? 0
        )}</span></div>
        <div class="summary-row"><span>Discount</span><span>-${currency(
          data.discount ?? 0
        )}</span></div>
        ${
          deposit > 0
            ? `<div class="summary-row"><span>Deposit</span><span>-${currency(
                deposit
              )}</span></div>`
            : ""
        }
        <div class="summary-row total"><span>Total Due</span><span>${currency(
          data.total ?? 0
        )}</span></div>
      </div>

      ${
        data.notes || data.terms
          ? `<div class="notes-box">
              ${
                data.notes
                  ? `<div><strong>Notes</strong><div>${escapeHtml(
                      data.notes
                    )}</div></div>`
                  : ""
              }
              ${
                data.terms
                  ? `<div><strong>Terms</strong><div>${escapeHtml(
                      data.terms
                    )}</div></div>`
                  : ""
              }
            </div>`
          : ""
      }

      <footer>
        <div><strong>Registered Address</strong><br/>${lines(
          data.address,
          data.city,
          data.postCode
        )}</div>
        ${
          lines(data.phoneNumber, data.email)
            ? `<div><strong>Contact</strong><br/>${lines(
                data.phoneNumber,
                data.email
              )}</div>`
            : ""
        }
        ${
          paymentFields(data).length
            ? `<div><strong>Payee</strong><br/>${paymentFields(data)
                .filter((row) => row.label !== "Reference")
                .map((row) => `${row.label}: ${row.value}`)
                .join("<br/>")}</div>`
            : ""
        }
      </footer>
    </div>
  </body>
</html>`;
}

function createCryptoHtml(data, accentColor) {
  const accent = accentColor || "#00ffff";
  const deposit = Number(data.deposit) || 0;
  const discount = Number(data.discount) || 0;

  return `
<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <title>Crypto Digital Invoice</title>
    <style>
      :root { --bg:#0d0d0d; --card:#161616; --accent:${escapeHtml(
        accent
      )}; --muted:#9ca3af; }
      @page { size:A4; margin:25mm; }
      @media screen {
        body .page { width: 900px; max-width: 100%; }
      }
      body { font-family:'JetBrains Mono', monospace; background:var(--bg); color:#fff; padding:50px; }
      .invoice { width:100%; max-width:900px; margin:auto; background:var(--card); border:1px solid #222; border-radius:12px; box-shadow:0 0 20px rgba(0,255,255,0.1); padding:40px; display:flex; flex-direction:column; gap:24px; min-height:100vh; box-sizing:border-box; }
      header { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:1px solid #333; padding-bottom:20px; }
      .logo-wrap { display:flex; align-items:center; gap:12px; }
      .invoice-number { color:var(--muted); font-size:13px; }
      .info { display:grid; grid-template-columns:1fr 1fr; margin-top:10px; gap:40px; }
      .info strong { display:block; color:var(--accent); margin-bottom:4px; }
      table { width:100%; border-collapse:collapse; margin-top:30px; }
      th, td { padding:10px 6px; border-bottom:1px solid #333; }
      th { color:var(--muted); text-align:left; }
      .right { text-align:right; }
      .summary { border-top:1px solid #333; margin-top:20px; padding-top:10px; }
      .summary div { display:flex; justify-content:space-between; margin:4px 0; }
      .total { color:var(--accent); font-weight:700; }
      .notes-box { background:#111; border:1px solid #222; border-radius:10px; padding:12px 14px; display:grid; gap:8px; color:var(--muted); }
      .notes-box strong { color:var(--accent); }
      footer { margin-top:auto; border-top:1px solid #333; padding-top:20px; font-size:13px; color:var(--muted); display:grid; grid-template-columns:repeat(auto-fit,minmax(220px,1fr)); gap:16px; }
      .wallet { color:var(--accent); font-size:13px; }
    </style>
  </head>
  <body>
    <div class="invoice">
      <header>
        <div class="logo-wrap">
          ${logoHtml(data, 44)}
          <div>${safe(data.companyName)}</div>
        </div>
        ${
          data.invoiceNumber
            ? `<div class="invoice-number">INV: ${safe(
                data.invoiceNumber
              )}</div>`
            : ""
        }
      </header>

      <div class="info">
        <div>
          <strong>From</strong>
          ${lines(data.companyName, data.address, [data.city, data.postCode])}
        </div>
        <div>
          <strong>To</strong>
          ${lines(
            data.customerName,
            data.customerAddress,
            [data.customerCity, data.customerPostCode],
            data.email,
            data.phoneNumber
          )}
        </div>
      </div>

      <div class="info">
        <div>
          <strong>Invoice</strong>
          ${lines(
            data.invoiceNumber && `Invoice #: ${data.invoiceNumber}`,
            data.invoiceDate && `Invoice Date: ${data.invoiceDate}`,
            data.suppliedDate && `Due Date: ${data.suppliedDate}`
          )}
        </div>
        <div>
          ${
            paymentFields(data).length
              ? `<strong>Payment</strong><br/>${paymentFields(data)
                  .map((row) => `${row.label}: ${row.value}`)
                  .join("<br/>")}`
              : ""
          }
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>Service</th>
            <th class="right">Qty</th>
            <th class="right">Rate (GBP)</th>
            <th class="right">Total (GBP)</th>
          </tr>
        </thead>
        <tbody>
          ${itemsRows(data, { dark: true })}
        </tbody>
      </table>

      <div class="summary">
        <div><span>Subtotal</span><span>${currency(
          data.subtotal ?? 0
        )}</span></div>
        ${
          discount > 0
            ? `<div><span>Discount</span><span>-${currency(
                discount
              )}</span></div>`
            : ""
        }
        ${
          deposit > 0
            ? `<div><span>Deposit</span><span>-${currency(
                deposit
              )}</span></div>`
            : ""
        }
        <div class="total"><span>Total Due</span><span>${currency(
          data.total ?? 0
        )}</span></div>
      </div>

      ${
        data.notes || data.terms
          ? `<div class="notes-box">
              ${
                data.notes
                  ? `<div><strong>Notes</strong><div>${escapeHtml(
                      data.notes
                    )}</div></div>`
                  : ""
              }
              ${
                data.terms
                  ? `<div><strong>Terms</strong><div>${escapeHtml(
                      data.terms
                    )}</div></div>`
                  : ""
              }
            </div>`
          : ""
      }

      <footer>
        ${
          paymentFields(data).length
            ? `<div><strong>Payment Details</strong><br/>${paymentFields(data)
                .map((row) => `${row.label}: ${row.value}`)
                .join("<br/>")}</div>`
            : ""
        }
        ${
          data.invoiceNumber
            ? `<div><strong>Reference</strong><div class="wallet">${safe(
                data.invoiceNumber
              )}</div></div>`
            : ""
        }
        ${
          lines(data.email, data.phoneNumber)
            ? `<div><strong>Contact</strong><br/>${lines(
                data.email,
                data.phoneNumber
              )}</div>`
            : ""
        }
      </footer>
    </div>
  </body>
</html>`;
}

function createFormalHtml(data) {
  const discount = Number(data.discount) || 0;
  const deposit = Number(data.deposit) || 0;
  return `
<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <title>Formal Invoice</title>
    <style>
      @page { size:A4; margin:25mm; }
      @media screen {
        .page {
          transform: scale(0.80);
          transform-origin: top center;
        }
      }
      body { font-family:'Times New Roman', serif; color:#000; background:#fff; padding:50px; }
      header { border-bottom:2px solid #000; padding-bottom:10px; margin-bottom:30px; }
      .firm { font-size:24px; font-weight:bold; }
      .ref { text-align:right; font-size:14px; }
      .details { display:flex; justify-content:space-between; margin-bottom:30px; font-size:14px; }
      table { width:100%; border-collapse:collapse; font-size:14px; }
      th, td { border:1px solid #000; padding:8px; }
      th { background:#f2f2f2; }
      .right { text-align:right; }
      .summary { margin-top:20px; width:50%; float:right; }
      .summary td { border:none; padding:4px 0; }
      .payment-box { margin-top:16px; width:50%; float:right; font-size:14px; }
      .notes-box { clear:both; margin-top:30px; border-top:1px solid #000; padding-top:10px; font-size:13px; }
      .notes-box strong { display:block; margin-bottom:4px; }
      .signature { margin-top:100px; text-align:right; }
      .signature-line { width:200px; border-top:1px solid #000; margin-left:auto; }
      footer { clear:both; margin-top:80px; border-top:1px solid #000; padding-top:10px; font-size:12px; }
    </style>
  </head>
  <body>
    <header>
      <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;">
        <div style="display:flex;align-items:center;gap:16px;">
          ${logoHtml(data, 48)}
          <div class="firm">${escapeHtml(data.companyName)}</div>
        </div>
        <div class="ref">Invoice Ref: ${safe(data.invoiceNumber)}${
    data.invoiceDate ? " | Date: " + safe(data.invoiceDate) : ""
  }${data.suppliedDate ? " | Due: " + safe(data.suppliedDate) : ""}</div>
      </div>
    </header>

    <div class="details">
      <div>
        <strong>To:</strong><br/>
        ${lines(
          data.customerName,
          data.customerAddress,
          [data.customerCity, data.customerPostCode],
          data.email,
          data.phoneNumber
        )}
      </div>
      <div>
        <strong>From:</strong><br/>
        ${lines(data.companyName, data.address, [data.city, data.postCode])}
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th class="right">Hours</th>
          <th class="right">Rate (£)</th>
          <th class="right">Amount (£)</th>
        </tr>
      </thead>
      <tbody>
        ${(data.items || [])
          .map(
            (it) =>
              `<tr><td>${escapeHtml(it.description)}</td><td class="right">${
                it.qty ?? 0
              }</td><td class="right">${fmt(
                it.unitPrice ?? 0
              )}</td><td class="right">${fmt(
                (it.qty || 0) * (it.unitPrice || 0)
              )}</td></tr>`
          )
          .join("")}
      </tbody>
    </table>

    <table class="summary">
      <tr><td><strong>Subtotal</strong></td><td class="right">${currency(
        data.subtotal ?? 0
      )}</td></tr>
      ${
        discount > 0
          ? `<tr><td>Discount</td><td class="right">-${currency(
              discount
            )}</td></tr>`
          : ""
      }
      ${
        deposit > 0
          ? `<tr><td>Deposit</td><td class="right">-${currency(
              deposit
            )}</td></tr>`
          : ""
      }
      <tr><td><strong>Total Due</strong></td><td class="right"><strong>${currency(
        data.total ?? 0
      )}</strong></td></tr>
    </table>

    ${
      paymentFields(data).length
        ? `<div class="payment-box">
            <strong>Payment Details</strong><br/>
            ${paymentFields(data)
              .map((row) => `${row.label}: ${row.value}`)
              .join("<br/>")}
          </div>`
        : ""
    }

    ${
      data.notes || data.terms
        ? `<div class="notes-box">
            ${
              data.notes
                ? `<div><strong>Notes</strong>${escapeHtml(data.notes)}</div>`
                : ""
            }
            ${
              data.terms
                ? `<div><strong>Terms</strong>${escapeHtml(data.terms)}</div>`
                : ""
            }
          </div>`
        : ""
    }

    

    <footer>
      ${lines(
        data.address,
        [data.city, data.postCode],
        data.email,
        data.phoneNumber
      )}
    </footer>
  </body>
</html>`;
}

function createModernHtml(data, accentColor) {
  const accent = accentColor || "#2b7bb9";
  const deposit = Number(data.deposit) || 0;
  const discount = Number(data.discount) || 0;
  const invoiceLabel = data.invoiceNumber
    ? "#" + data.invoiceNumber
    : "Invoice";

  return `
<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta
      name="viewport"
      content="width=device-width, initial-scale=1.0"
    />
    <title>Invoice</title>
    <style>
      :root {
        --accent:${escapeHtml(accent)};
        --accent-soft:#e0f2fe;
        --text:#111827;
        --muted:#6b7280;
        --border:#e5e7eb;
        --bg-page:#f3f4f6;
        --bg-light:#f9fafb;
      }

      @page {
        size:A4;
        margin:18mm;
      }
      @media screen {
        body .page { width: 900px; max-width: 100%; }
      }

      *,
      *::before,
      *::after {
        box-sizing:border-box;
      }

      body {
        margin:0;
        padding:24px;
        background:var(--bg-page);
        font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
        color:var(--text);
        font-size:13px;
        line-height:1.6;
      }

      .page {
        max-width:900px;
        width:100%;
        margin:0 auto;
        background:#fff;
        border-radius:10px;
        padding:28px 32px 32px;
        box-shadow:0 18px 45px rgba(15,23,42,0.12);
        box-sizing:border-box;
      }

      @media print {
        body {
          background:#fff;
          padding:0;
        }
        .page {
          box-shadow:none;
          border-radius:0;
          max-width:100%;
          margin:0;
          padding:0;
        }
      }

      h1 {
        color:var(--accent);
        font-size:32px;
        letter-spacing:0.08em;
        text-transform:uppercase;
        margin:0 0 8px;
      }

      .header {
        display:flex;
        justify-content:space-between;
        align-items:flex-start;
        gap:24px;
      }

      .header-left {
        display:flex;
        flex-direction:column;
        gap:4px;
      }

      .subtitle {
        font-size:13px;
        color:var(--muted);
      }

      .badge {
        display:inline-flex;
        align-items:center;
        padding:3px 10px;
        border-radius:999px;
        font-size:11px;
        text-transform:uppercase;
        letter-spacing:0.08em;
        background:var(--accent-soft);
        color:var(--accent);
        margin-top:6px;
      }

      .logo {
        width:72px;
        height:72px;
        border-radius:50%;
        overflow:hidden;
        display:flex;
        align-items:center;
        justify-content:center;
        background:var(--bg-light);
      }

      .divider {
        border-top:2px solid var(--border);
        margin:18px 0 24px;
      }

      .address-row {
        display:flex;
        justify-content:space-between;
        align-items:flex-start;
        gap:24px;
        margin-bottom:24px;
      }

      .address {
        flex:1;
        font-size:13px;
      }

      .address strong {
        font-size:14px;
      }

      .details-box {
        flex:1;
        background:var(--bg-light);
        border-radius:8px;
        padding:10px 14px;
        font-size:12px;
      }

      .details-grid {
        display:grid;
        grid-template-columns:auto 1fr;
        row-gap:4px;
        column-gap:8px;
      }

      .details-label {
        font-weight:600;
        color:var(--muted);
      }

      .details-value {
        text-align:right;
      }

      .notes-box {
        flex:1;
        background:var(--bg-light);
        border-radius:8px;
        padding:10px 14px;
        font-size:12px;
        border-left:3px solid var(--accent);
        white-space:pre-line;
      }

      .notes-box h3 {
        margin:0 0 6px;
        font-size:12px;
        text-transform:uppercase;
        letter-spacing:0.08em;
        color:var(--muted);
      }

      .table {
        width:100%;
        border-collapse:collapse;
        margin-top:8px;
        margin-bottom:24px;
      }

      .table th,
      .table td {
        padding:9px 6px;
        border-bottom:1px solid var(--border);
        vertical-align:top;
      }

      .table th {
        background:var(--bg-light);
        font-size:12px;
        text-align:left;
        font-weight:600;
        color:var(--muted);
      }

      .table tbody tr:nth-child(even) {
        background:#f9fafb;
      }

      .right {
        text-align:right;
        white-space:nowrap;
      }

      .summary-wrap {
        display:flex;
        justify-content:flex-end;
        gap:24px;
        align-items:flex-start;
        margin-top:4px;
      }

      .summary {
        border-collapse:collapse;
        font-size:13px;
        min-width:220px;
      }

      .summary td {
        padding:4px 0;
      }

      .summary .label {
        text-align:right;
        color:var(--muted);
        padding-right:10px;
      }

      .summary .total-row {
        border-top:2px solid var(--border);
        padding-top:6px;
      }

      .summary .total {
        color:var(--accent);
        font-weight:700;
        font-size:16px;
      }

      footer {
        margin-top:28px;
        border-top:2px solid var(--border);
        padding-top:14px;
        display:flex;
        gap:18px;
        justify-content:space-between;
        font-size:11px;
        color:var(--muted);
      }

      footer div {
        flex:1;
      }

      footer strong {
        display:block;
        font-size:11px;
        margin-bottom:4px;
        text-transform:uppercase;
        letter-spacing:0.08em;
      }

    </style>
  </head>
  <body>
    <div class="page">
      <header class="header">
        <div class="header-left">
          <h1>Invoice</h1>
          <div class="subtitle">
            ${escapeHtml(data.companyName || "")}
          </div>
          <span class="badge">
            ${escapeHtml(invoiceLabel)}
          </span>
        </div>
        <div class="logo">
          ${logoHtml(data, 70)}
        </div>
      </header>

      <div class="divider"></div>

      <div class="address-row">
        <div class="address">
          <strong>From</strong><br/>
          ${lines(data.companyName, data.address, [data.city, data.postCode])}
        </div>
        <div class="details-box">
          <div class="details-grid">
            <div class="details-label">Invoice date</div>
            <div class="details-value">${escapeHtml(data.invoiceDate)}</div>

            <div class="details-label">Invoice number</div>
            <div class="details-value">${escapeHtml(data.invoiceNumber)}</div>

            <div class="details-label">Supplied date</div>
            <div class="details-value">${escapeHtml(data.suppliedDate)}</div>

            <div class="details-label">Due date</div>
            <div class="details-value">${escapeHtml(data.suppliedDate)}</div>
          </div>
        </div>
      </div>

      <div class="address-row">
        <div class="address">
          <strong>Bill to</strong><br/>
          ${lines(
            data.customerName,
            data.customerAddress,
            [data.customerCity, data.customerPostCode],
            data.phoneNumber ? `Phone: ${data.phoneNumber}` : "",
            data.email ? `Email: ${data.email}` : ""
          )}
        </div>
        ${
          data.notes || data.terms
            ? `<div class="notes-box">
                ${data.notes ? `<h3>Notes</h3>${escapeHtml(data.notes)}` : ""}
                ${
                  data.terms
                    ? '<h3 style="margin-top:10px;">Terms</h3>' +
                      escapeHtml(data.terms)
                    : ""
                }
              </div>`
            : ""
        }
      </div>

      <table class="table">
        <thead>
          <tr>
            <th>Description</th>
            <th>Qty</th>
            <th class="right">Unit price</th>
            <th class="right">Line total</th>
          </tr>
        </thead>
        <tbody>
          ${itemsRows(data)}
        </tbody>
      </table>

      <div class="summary-wrap">
        <table class="summary">
          <tr>
            <td class="label" style="width:80%">Sub total:</td>
            <td class="right">${currency(data.subtotal ?? 0)}</td>
          </tr>
          ${
            discount > 0
              ? '<tr><td class="label">Discount:</td><td class="right">-' +
                currency(discount) +
                "</td></tr>"
              : ""
          }
          ${
            deposit > 0
              ? '<tr><td class="label">Deposit:</td><td class="right">-' +
                currency(deposit) +
                "</td></tr>"
              : ""
          }
          <tr class="total-row">
            <td class="label total">Total amount due:</td>
            <td class="right total">${currency(data.total ?? 0)}</td>
          </tr>
        </table>
      </div>

      <footer>
        <div>
          <strong>Registered address</strong>
          ${lines(data.address, [data.city, data.postCode])}
        </div>
        ${
          lines(
            data.phoneNumber ? `Phone: ${data.phoneNumber}` : "",
            data.email ? `Email: ${data.email}` : ""
          )
            ? `<div>
          <strong>Contact</strong>
          ${lines(
            data.phoneNumber ? `Phone: ${data.phoneNumber}` : "",
            data.email ? `Email: ${data.email}` : ""
          )}
        </div>`
            : ""
        }
        ${
          paymentFields(data).length
            ? `<div>
                <strong>Payment details</strong><br/>
                ${paymentFields(data)
                  .map((row) => `${row.label}: ${row.value}`)
                  .join("<br/>")}
              </div>`
            : ""
        }
      </footer>
    </div>
  </body>
</html>`;
}

function createOldHtml(data) {
  const deposit = Number(data.deposit) || 0;

  return `
<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Spreadsheet Style Invoice</title>
    <style>
      @page { size:A4; margin:25mm; }
      body { font-family:'Courier New', monospace; color:#000; background:#fff; padding:40px; font-size:13px; }
      table { border-collapse:collapse; width:100%; margin-bottom:25px; }
      th, td { border:1px solid #999; padding:6px 8px; vertical-align:top; }
      th { background:#f2f2f2; font-weight:bold; text-align:left; }
      .header-table { margin-bottom:40px; border:2px solid #000; }
      .header-table td { border:1px solid #000; }
      .section-title { background:#ddd; font-weight:bold; text-transform:uppercase; padding:6px; }
      .label { width:25%; font-weight:bold; }
      .right { text-align:right; }
      .center { text-align:center; }
      footer { margin-top:40px; border-top:2px solid #000; padding-top:10px; font-size:12px; }
    </style>
  </head>
  <body>
    <table class="header-table">
      <tr>
        <td colspan="3" class="center" style="font-size:18px;font-weight:bold">INVOICE</td>
        <td class="center">${logoHtml(data, 40)}</td>
      </tr>
      <tr><td class="label">Company Name</td><td>${escapeHtml(
        data.companyName
      )}</td><td class="label">Invoice No.</td><td>${escapeHtml(
    data.invoiceNumber
  )}</td></tr>
      <tr><td class="label">Address</td><td>${escapeHtml(
        data.address
      )}, ${escapeHtml(data.city)} ${escapeHtml(
    data.postCode
  )}</td><td class="label">Invoice Date</td><td>${escapeHtml(
    data.invoiceDate
  )}</td></tr>
      <tr><td class="label">Phone</td><td>${escapeHtml(
        data.phoneNumber
      )}</td><td class="label">Due Date</td><td>${escapeHtml(
    data.suppliedDate
  )}</td></tr>
    </table>

    <table>
      <tr><td colspan="4" class="section-title">Bill To</td></tr>
      <tr><td class="label">Customer</td><td>${escapeHtml(
        data.customerName
      )}</td><td class="label">Email</td><td>${escapeHtml(data.email)}</td></tr>
      <tr><td class="label">Phone</td><td>${escapeHtml(
        data.phoneNumber
      )}</td><td></td><td></td></tr>
      <tr>
        <td class="label">Address</td>
        <td colspan="3">${
          escapeHtml(data.customerAddress || "") +
          (data.customerCity || data.customerPostCode
            ? ", " +
              escapeHtml(
                [data.customerCity || "", data.customerPostCode || ""]
                  .filter(Boolean)
                  .join(" ")
              )
            : "")
        }</td>
      </tr>
    </table>

    <table>
      <tr><th>Description</th><th class="center">Qty</th><th class="right">Unit Price</th><th class="right">Total</th></tr>
      ${(data.items || [])
        .map(
          (it) =>
            `<tr><td>${escapeHtml(it.description)}</td><td class="center">${
              it.qty || 0
            }</td><td class="right">${currency(
              it.unitPrice || 0
            )}</td><td class="right">${currency(
              (it.qty || 0) * (it.unitPrice || 0)
            )}</td></tr>`
        )
        .join("")}
    </table>

    <table>
      <tr><td colspan="3" class="right"><strong>Sub Total</strong></td><td class="right">${currency(
        data.subtotal ?? 0
      )}</td></tr>
      <tr><td colspan="3" class="right"><strong>Discount</strong></td><td class="right">-${currency(
        data.discount ?? 0
      )}</td></tr>
      ${
        deposit > 0
          ? `<tr><td colspan="3" class="right"><strong>Deposit</strong></td><td class="right">-${currency(
              deposit
            )}</td></tr>`
          : ""
      }
      <tr><td colspan="3" class="right"><strong>Total Amount Due</strong></td><td class="right"><strong>${currency(
        data.total ?? 0
      )}</strong></td></tr>
    </table>

    <table>
      <tr><td colspan="4" class="section-title">Payment Details</td></tr>
      ${
        data.payee
          ? `<tr><td class="label">Payee</td><td colspan="3">${escapeHtml(
              data.payee
            )}</td></tr>`
          : ""
      }
      <tr><td class="label">Sort Code</td><td>${escapeHtml(
        data.sortCode
      )}</td><td class="label">Account Number</td><td>${escapeHtml(
    data.accountNumber
  )}</td></tr>
    </table>

    <table>
      <tr><td colspan="4" class="section-title">Notes</td></tr>
      <tr><td colspan="4">${
        data.notes
          ? [data.notes ? escapeHtml(data.notes) : ""]
              .filter(Boolean)
              .join("<br/><br/>")
          : ""
      }</td>
            <tr><td colspan="4" class="section-title">Terms</td></tr>
      <td colspan="4">${
        data.terms
          ? [data.terms ? escapeHtml(data.terms) : ""]
              .filter(Boolean)
              .join("<br/><br/>")
          : ""
      }</td>
      </tr>
    </table>

    <footer>
      Registered Address: ${escapeHtml(data.address)}, ${escapeHtml(
    data.city
  )}, ${escapeHtml(data.postCode)} | Email: ${escapeHtml(
    data.email
  )} | Phone: ${escapeHtml(data.phoneNumber)}
    </footer>
  </body>
</html>`;
}

function createTechHtml(data, accentColor) {
  const accent = accentColor || "#0d6efd";
  const deposit = Number(data.deposit) || 0;

  return `
<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Tech Startup Invoice</title>
    <style>
      :root { --accent:${escapeHtml(
        accent
      )}; --muted:#6b7280; --border:#e5e7eb; --bg:#f9fafb; --text:#111827; }
      @page { size:A4; margin:25mm; }
      @media screen {
        body .page { width: 900px; max-width: 100%; }
      }
      body { font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background:var(--bg); color:var(--text); margin:0; padding:40px; }
      .invoice-wrapper { background:#fff; border-radius:16px; box-shadow:0 4px 12px rgba(0,0,0,0.08); padding:40px 50px; max-width:900px; width:100%; margin:0 auto; box-sizing:border-box; }
      .header { display:flex; justify-content:space-between; align-items:flex-start; }
      .company { font-weight:700; font-size:20px; }
      .logo { width:60px; height:60px; border-radius:12px; overflow:hidden; display:flex; align-items:center; justify-content:center; }
      .invoice-title { font-size:36px; font-weight:800; margin:10px 0 20px; color:var(--accent); }
      .meta { display:grid; grid-template-columns:1fr 1fr; gap:40px; margin-bottom:30px; }
      .info-card { background:#f3f4f6; border-radius:10px; padding:20px; }
      .info-card strong { display:block; margin-bottom:4px; font-weight:600; }
      table { width:100%; border-collapse:collapse; margin-top:20px; }
      th, td { padding:12px 10px; border-bottom:1px solid var(--border); }
      th { text-align:left; color:var(--muted); font-weight:600; font-size:14px; }
      td { font-size:14px; }
      .right { text-align:right; }
      .summary { margin-top:30px; width:100%; border-top:2px solid var(--border); padding-top:10px; }
      .summary-row { display:flex; justify-content:space-between; margin:6px 0; }
      .summary-row strong { font-weight:700; }
      .total { font-size:18px; color:var(--accent); font-weight:800; }
      footer { margin-top:50px; border-top:2px solid var(--border); padding-top:20px; display:flex; justify-content:space-between; font-size:13px; color:var(--muted); }
      footer div { width:32%; }
      .pay-btn { margin-top:20px; display:inline-block; background:var(--accent); color:#fff; font-weight:600; padding:10px 20px; border-radius:8px; text-decoration:none; font-size:14px; }
    </style>
  </head>
  <body>
    <div class="invoice-wrapper">
      <div class="header">
        <div>
          <div class="company">${escapeHtml(data.companyName)}</div>
          <div class="invoice-title">Invoice</div>
        </div>
        <div class="logo">
          ${logoHtml(data, 60)}
        </div>
      </div>

      <div class="meta">
        <div>
          <strong>From:</strong>
          ${escapeHtml(data.companyName)}<br/>
          ${escapeHtml(data.address)}<br/>
          ${escapeHtml(data.city)}, ${escapeHtml(data.postCode)}
        </div>
        <div class="info-card">
          <strong>Invoice No:</strong> ${escapeHtml(data.invoiceNumber)}<br/>
          <strong>Invoice Date:</strong> ${escapeHtml(data.invoiceDate)}<br/>
          <strong>Due Date:</strong> ${escapeHtml(data.suppliedDate)}
        </div>
      </div>

      <div class="meta" style="margin-top:10px;">
        <div>
          <strong>Bill To:</strong>
          ${escapeHtml(data.customerName)}<br/>
          ${escapeHtml(data.customerAddress || "")}<br/>
          ${escapeHtml(
            [data.customerCity || "", data.customerPostCode || ""]
              .filter(Boolean)
              .join(" ")
          )}<br/>
          ${escapeHtml(data.email)}<br/>
          ${escapeHtml(data.phoneNumber)}
        </div>
        <div>
          ${
            paymentFields(data).length
              ? `<strong>Payment Details:</strong><br/>
              ${paymentFields(data)
                .map((row) => `${row.label}: ${row.value}`)
                .join("<br/>")}`
              : ""
          }
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th class="right">Qty</th>
            <th class="right">Unit Price</th>
            <th class="right">Total</th>
          </tr>
        </thead>
        <tbody>
          ${itemsRows(data)}
        </tbody>
      </table>

      <div class="summary">
        <div class="summary-row"><span>Subtotal</span><span>${currency(
          data.subtotal ?? 0
        )}</span></div>
        <div class="summary-row"><span>Discount</span><span>-${currency(
          data.discount ?? 0
        )}</span></div>
        ${
          deposit > 0
            ? `<div class="summary-row"><span>Deposit</span><span>-${currency(
                deposit
              )}</span></div>`
            : ""
        }
        <div class="summary-row total"><span>Total Due</span><span>${currency(
          data.total ?? 0
        )}</span></div>
      </div>

      ${
        data.notes
          ? `<div class="info-card" style="margin-top:12px;">
              ${
                data.notes
                  ? `<strong>Notes</strong><div>${escapeHtml(data.notes)}</div>`
                  : ""
              }
              
            </div>`
          : ""
      }
      ${
        data.terms
          ? `<div class="info-card" style="margin-top:12px;">
              ${
                data.notes
                  ? `<strong>Terms</strong><div>${escapeHtml(data.terms)}</div>`
                  : ""
              }
              
            </div>`
          : ""
      }

      <footer>
        <div><strong>Registered Address</strong><br/>${lines(data.address, [
          data.city,
          data.postCode,
        ])}</div>
        ${
          lines(data.phoneNumber, data.email)
            ? `<div><strong>Contact</strong><br/>${lines(
                data.phoneNumber,
                data.email
              )}</div>`
            : ""
        }
        
      </footer>
    </div>
  </body>
</html>`;
}
